import { emptyStateAlertGraphic } from '@workday/canvas-graphics-web';
import type { ButtonColors } from '@workday/canvas-kit-react/button';
import { Flex } from '@workday/canvas-kit-react/layout';
import { createStyles, px2rem } from '@workday/canvas-kit-styling';
import { WcpStyles } from '@workday/wcp-tokens';
import React from 'react';
import routes from 'config/developerSite/routes';
import { EMPTY_STATE_DESCRIPTION_CLASS, EMPTY_STATE_TITLE_CLASS } from 'modules/common/components/Empty';
import EmptyWithButtons from 'modules/common/components/EmptyWithButtons';
import { SecondaryLinkButton } from 'modules/common/components/LinkButton';
import { navbarHeight } from '../../utils/devsite';

export const ERROR_404_TEST_ID = '404-error';
const BACKGROUND_GRADIENT = 'linear-gradient(0deg, #6923b6 3%, #3f2b98 37%, #241d91 64%, #000028 97%)';
/** Darkest gradient stop — fills any gap below `background-image` in some browsers. */
const BACKGROUND_COLOR = '#000028';

/** Outline CTA on the fixed dark 404 gradient — matches `/errors/error.css` hover invert. */
const errorHomeButtonColors: ButtonColors = {
  default: {
    background: 'transparent',
    border: WcpStyles.color.fg.inverseException,
    icon: WcpStyles.color.fg.inverseException,
    label: WcpStyles.color.fg.inverseException,
  },
  focus: {
    background: WcpStyles.color.fg.inverseException,
    border: WcpStyles.color.fg.inverseException,
    icon: BACKGROUND_COLOR,
    label: BACKGROUND_COLOR,
  },
  hover: {
    background: WcpStyles.color.fg.inverseException,
    border: WcpStyles.color.fg.inverseException,
    icon: BACKGROUND_COLOR,
    label: BACKGROUND_COLOR,
  },
  active: {
    background: WcpStyles.color.fg.inverseException,
    border: WcpStyles.color.fg.inverseException,
    icon: BACKGROUND_COLOR,
    label: BACKGROUND_COLOR,
  },
  disabled: {
    background: 'transparent',
    border: WcpStyles.color.fg.inverseException,
    icon: WcpStyles.color.fg.inverseException,
    label: WcpStyles.color.fg.inverseException,
    opacity: '40%',
  },
};

const Error404: React.FC = () => (
  <Flex data-cy={ERROR_404_TEST_ID} data-testid={ERROR_404_TEST_ID} className={errorContainerStyles}>
    <EmptyWithButtons
      graphicOverride={emptyStateAlertGraphic}
      title='Page Not Found'
      description='The page you requested was not found. It may have been removed or renamed.'
    >
      <SecondaryLinkButton colors={errorHomeButtonColors} to={routes.home}>
        Return to Home
      </SecondaryLinkButton>
    </EmptyWithButtons>
  </Flex>
);

export default Error404;

const errorContainerStyles = createStyles({
  alignItems: 'center',
  backgroundColor: BACKGROUND_COLOR,
  backgroundImage: BACKGROUND_GRADIENT,
  boxSizing: 'border-box',
  display: 'flex',
  flexGrow: 1,
  justifyContent: 'center',
  minHeight: `calc(100vh - ${px2rem(navbarHeight)})`,
  width: '100%',
  '#developer-site:has(&)': {
    backgroundColor: BACKGROUND_COLOR,
    backgroundImage: BACKGROUND_GRADIENT,
  },
  [`& .${EMPTY_STATE_TITLE_CLASS}`]: {
    color: WcpStyles.color.fg.inverseException,
  },
  [`& .${EMPTY_STATE_DESCRIPTION_CLASS}`]: {
    color: WcpStyles.color.fg.inverseException,
  },
});
